import { monitoringCoverageUnavailable } from "../../../shared/aiMonitoring";
import { useAuth } from "@/_core/hooks/useAuth";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { trpc } from "@/lib/trpc";
import { Globe2, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";
import { Link } from "wouter";
import { toast } from "sonner";

type AiProvider = "aio" | "gemini" | "chatgpt";

const PROVIDERS: AiProvider[] = ["aio", "gemini", "chatgpt"];

function getProviderLabel(provider: string) {
  switch (provider) {
    case "aio":
    case "google_ai_overview":
      return "AIO";
    case "gemini":
      return "Gemini";
    case "chatgpt":
      return "ChatGPT";
    default:
      return provider;
  }
}

function getCitationTypeLabel(type: string) {
  switch (type) {
    case "brand_mentioned":
      return "品牌提及";
    case "brand_linked":
      return "品牌連結";
    default:
      return type;
  }
}

function getChangeLabel(type: string) {
  switch (type) {
    case "stable_gain":
      return "穩定新增";
    case "stable_loss":
      return "穩定流失";
    case "stable_link_gain":
      return "連結新增";
    case "stable_link_loss":
      return "連結流失";
    default:
      return type;
  }
}

export default function AiPerformance() {
  const { loading } = useAuth();
  const [days, setDays] = useState<7 | 30 | 90>(30);
  const [providerFilter, setProviderFilter] = useState<string>("all");
  const monitoringQuery = trpc.dashboard.getAiMonitoringStatus.useQuery(undefined, { refetchInterval: 15_000 });
  const { data, isLoading, error: reportError } = trpc.dashboard.getAiPerformanceReport.useQuery({
    days,
    provider: providerFilter === "all" ? undefined : providerFilter,
  }, { refetchInterval: 15_000 });
  const { data: changeData } = trpc.dashboard.getAiChangeReport.useQuery({
    days,
    provider: providerFilter === "all" ? undefined : (providerFilter as AiProvider),
  });
  const exportQuery = trpc.dashboard.exportAiMonitoring.useQuery(
    {
      days,
      provider: providerFilter === "all" ? undefined : (providerFilter as AiProvider),
      format: "csv",
    },
    { enabled: false }
  );

  const trend = useMemo(
    () =>
      (data?.trend || []).map((point: any) => ({
        ...point,
        dateLabel: point.date?.slice(5).replace("-", "/"),
      })),
    [data?.trend]
  );

  const providerMetrics = useMemo(() => {
    const metricMap = new Map<string, any>();
    for (const metric of data?.providerMetrics || []) {
      metricMap.set(metric.provider, metric);
    }
    return PROVIDERS.map((provider) => ({
      provider,
      citedKeywords: metricMap.get(provider)?.citedKeywords || 0,
      citations: metricMap.get(provider)?.citations || 0,
      coverageRate: metricMap.get(provider)?.coverageRate || 0,
    }));
  }, [data?.providerMetrics]);

  const providerOptions = useMemo(() => ["all", ...PROVIDERS], []);

  const exportCsv = async () => {
    const res = await exportQuery.refetch();
    const content = res.data?.content;
    if (!content || typeof content !== "string") {
      toast.error("匯出失敗：無可用資料");
      return;
    }
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ai-monitoring-${days}d-${providerFilter}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-8">載入中...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container py-8 space-y-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold">AI 引用表現</h1>
            <p className="text-muted-foreground">AIO / Gemini / ChatGPT 同層比較</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {[7, 30, 90].map((v) => (
              <Button key={v} size="sm" variant={days === v ? "default" : "outline"} onClick={() => setDays(v as 7 | 30 | 90)}>
                {v} 天
              </Button>
            ))}
            <select
              className="h-9 rounded-md border bg-background px-2 text-sm"
              value={providerFilter}
              onChange={(e) => setProviderFilter(e.target.value)}
            >
              {providerOptions.map((item) => (
                <option key={item} value={item}>{item === "all" ? "明細來源：全部" : `明細來源：${getProviderLabel(item)}`}</option>
              ))}
            </select>
            <Link href="/ai-performance/evidence">
              <Button size="sm" variant="outline">證據留痕</Button>
            </Link>
            <Button size="sm" variant="outline" onClick={exportCsv}>匯出 CSV</Button>
          </div>
        </div>

        <Card>
          <CardHeader><CardTitle>監控設定與最新檢查</CardTitle></CardHeader>
          <CardContent>
            {monitoringQuery.isLoading ? <p>載入監控狀態中</p> : monitoringQuery.error ? <p role="alert">監控狀態讀取失敗：{monitoringQuery.error.message}</p> : <>
              <p>品牌名稱：{monitoringQuery.data?.brandName || "尚未設定"}</p>
              <p>自動檢查：{({daily:"每日",weekly:"每週一",monthly:"每月一日",never:"停用"} as Record<string,string>)[monitoringQuery.data?.checkFrequency || ""] || "尚未設定"}，台灣時間 02:00；未啟動時於 06:00 補跑</p>
              {monitoringQuery.data?.providers.map(provider => <div key={provider.provider} className="mt-3 rounded border p-3 text-sm">
                <p>{getProviderLabel(provider.provider)}：成功 {provider.checkedKeywords}/{monitoringQuery.data.totalKeywords}，失敗 {provider.failedKeywords}，未檢查 {provider.missingKeywords}</p>
                <p>最後檢查時間：{provider.lastCheckedAt ? new Date(provider.lastCheckedAt).toLocaleString("zh-TW", {timeZone:"Asia/Taipei"}) + "（台灣時間）" : "尚無紀錄"}</p>
                {provider.latestError ? <p className="mt-1 text-amber-700">{provider.latestError}</p> : null}
              </div>)}
            </>}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>AI 來源覆蓋率（固定分母）</CardTitle>
            <CardDescription>分母固定為全部追蹤關鍵字：{data?.totalTrackedKeywords || 0}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              {providerMetrics.map((metric) => (
                <Card key={metric.provider}>
                  <CardHeader className="pb-2">
                    <CardDescription>{getProviderLabel(metric.provider)}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{monitoringCoverageUnavailable(monitoringQuery.data?.providers.find(row=>row.provider===metric.provider), monitoringQuery.isLoading || isLoading, Boolean(monitoringQuery.error || reportError) || !data) ? "無有效檢查結果" : `${metric.coverageRate}%`}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {monitoringCoverageUnavailable(monitoringQuery.data?.providers.find(row=>row.provider===metric.provider), monitoringQuery.isLoading || isLoading, Boolean(monitoringQuery.error || reportError) || !data) ? "請查看上方檢查狀態" : `${metric.citedKeywords} / ${data?.totalTrackedKeywords || 0} 關鍵字`}
                    </div>
                    <div className="text-sm mt-3">
                      引用次數 <span className="font-semibold">{metric.citations}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>穩定變化追蹤（7天視窗 + 3天確認）</CardTitle>
            <CardDescription>抗波動規則，只顯示穩定事件</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-3 md:grid-cols-4 text-sm">
              <div>穩定新增：{changeData?.summary?.stableGain || 0}</div>
              <div>穩定流失：{changeData?.summary?.stableLoss || 0}</div>
              <div>連結新增：{changeData?.summary?.stableLinkGain || 0}</div>
              <div>連結流失：{changeData?.summary?.stableLinkLoss || 0}</div>
            </div>
            {(changeData?.items || []).length ? (
              <div className="space-y-2 max-h-[260px] overflow-auto">
                {(changeData?.items || []).slice(0, 20).map((item: any, idx: number) => (
                  <div key={`${item.keywordId}-${item.provider}-${idx}`} className="rounded border px-3 py-2 text-sm">
                    <div className="font-medium">{item.keyword}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {getProviderLabel(item.provider)} · {String(item.intent || "unknown")} · {getChangeLabel(item.eventType)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">目前沒有穩定變化事件</div>
            )}
          </CardContent>
        </Card>

        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader className="pb-2"><CardDescription>被引用關鍵字數</CardDescription></CardHeader>
            <CardContent className="space-y-3">
              <div className="text-2xl font-bold flex items-center gap-2"><Search className="h-4 w-4 text-emerald-600" />{data?.groundingQueries || 0}</div>
              <Link href="/ai-performance/keywords">
                <Button size="sm" variant="outline" className="w-full">查看完整關鍵字列表</Button>
              </Link>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardDescription>被引用頁面數</CardDescription></CardHeader>
            <CardContent className="space-y-3">
              <div className="text-2xl font-bold flex items-center gap-2"><Globe2 className="h-4 w-4 text-orange-600" />{data?.citedPages || 0}</div>
              <Link href="/ai-performance/pages">
                <Button size="sm" variant="outline" className="w-full">查看完整頁面列表</Button>
              </Link>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>可見度趨勢</CardTitle>
            <CardDescription>引用量與被引用頁面的日趨勢（受明細來源篩選影響）</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-sm text-muted-foreground">載入中...</div>
            ) : (
              <ChartContainer
                className="h-[300px] w-full"
                config={{
                  citations: { label: "引用次數", color: "hsl(var(--chart-1))" },
                  citedPages: { label: "被引用頁數", color: "hsl(var(--chart-2))" },
                }}
              >
                <LineChart data={trend}>
                  <CartesianGrid vertical={false} />
                  <XAxis dataKey="dateLabel" tickLine={false} axisLine={false} />
                  <YAxis allowDecimals={false} tickLine={false} axisLine={false} />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Line type="monotone" dataKey="citations" stroke="var(--color-citations)" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="citedPages" stroke="var(--color-citedPages)" strokeWidth={2} dot={false} />
                </LineChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>

        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader><CardTitle>來源引用次數分布</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {(data?.providerBreakdown || []).length ? (
                (data?.providerBreakdown || []).map((row: any) => (
                  <div key={row.key} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">{getProviderLabel(row.key)}</span>
                      <span className="text-muted-foreground">{row.citations} ({row.percentage}%)</span>
                    </div>
                    <div className="h-2 bg-muted rounded">
                      <div className="h-2 bg-primary rounded" style={{ width: `${Math.min(100, row.percentage)}%` }} />
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-sm text-muted-foreground">暫無資料</div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>品牌訊號</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {(data?.aiCitationTypeBreakdown || []).length ? (
                (data?.aiCitationTypeBreakdown || []).map((row: any) => (
                  <div key={row.key} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">{getCitationTypeLabel(row.key)}</span>
                      <span className="text-muted-foreground">{row.citations} ({row.percentage}%)</span>
                    </div>
                    <div className="h-2 bg-muted rounded">
                      <div className="h-2 bg-chart-2 rounded" style={{ width: `${Math.min(100, row.percentage)}%` }} />
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-sm text-muted-foreground">暫無資料</div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
